import { useEffect, useState } from "react";

import type { MfaAuthenticator } from "react-native-auth0";

import { LoginTestID } from "@/constants/TestID";
import { router } from "@/crossPlatform/expo-router";
import useAuth from "@/hooks/useAuth";
import { useToast } from "@/hooks/useToast";
import { useAppStartupDispatch } from "@/store/reducers/appStartup";
import { useMFASelector } from "@/store/reducers/mfa";
import LoginScreenContent from "@/ui/components/authentication/LoginScreenContent";
import { Button, FAIconName, IconButton, TextField } from "@/ui/core";
import { HStack, KeyboardAvoidingView, VStack } from "@/ui/layout";
import { getPreferredAuthenticator } from "@/utils/mfa.util";
import { joinTestIDs } from "@/utils/string.util";

const handleBackNavigation = () => {
  router.back();
};

const MultiFactorAuthForm = () => {
  const { authorizeWithOOB, isLoading } = useAuth();
  const { setCanLoggedInUserSeeAppContent } = useAppStartupDispatch();

  const { mfaToken } = useMFASelector();
  const { getAuthenticators } = useAuth();
  const [authenticators, setAuthenticators] = useState<MfaAuthenticator[]>();

  useEffect(() => {
    const fetchAuthenticators = async () => {
      const response = await getAuthenticators({ mfaToken });
      if (response.data) setAuthenticators(response.data);
    };
    if (!mfaToken) return;
    void fetchAuthenticators();
  }, [getAuthenticators, mfaToken]);

  const { showToast } = useToast();
  const [mfaCode, setMfaCode] = useState<string>("");

  const isButtonDisabled = mfaCode.length !== 6;

  const handleSubmitMFA = async () => {
    const { data: credentials, error } = await authorizeWithOOB(mfaCode);
    if (credentials) {
      setCanLoggedInUserSeeAppContent(false);
    }

    if (!error) return;
    showToast({ text: error.message, variant: "error" });
  };

  const preferredAuthenticator = getPreferredAuthenticator(authenticators);

  return (
    <LoginScreenContent
      subtitle={`Enter the 6-digit code we've just sent to ${preferredAuthenticator?.name}`}
      testID={LoginTestID.MFA}
      title="Enter verification code"
    >
      <KeyboardAvoidingView>
        <VStack gap={24}>
          <TextField
            autoComplete="sms-otp"
            isBorderless
            keyboardType="number-pad"
            onChange={(value) => setMfaCode(value)}
            placeholder="••••••"
            testID={LoginTestID.MFA}
            value={mfaCode}
          />
          <HStack gap={24}>
            <IconButton
              iconName={FAIconName.ArrowLeft}
              onPress={handleBackNavigation}
              shouldShowBorder
              testID={joinTestIDs(LoginTestID.MFA, "back")}
            />
            <Button
              isDisabled={isButtonDisabled}
              isFullWidth
              isLoading={isLoading}
              leftIcon={FAIconName.ArrowRight}
              onPress={handleSubmitMFA}
              testID={joinTestIDs(LoginTestID.MFA, "submit")}
              title="Submit"
              variant="primary.on_background"
            />
          </HStack>
        </VStack>
      </KeyboardAvoidingView>
    </LoginScreenContent>
  );
};

export default MultiFactorAuthForm;
